import {
  OnAuthenticated,
  Renderers,
  LoginElementBuilderArgs,
} from './contracts';
import { buildFallbackRoute, buildSignInPath } from './utils';
import { useOktaAuthManager, useOktaAuthnAppContext } from '../contexts';
import {
  getDefaultOktaClientConfiguration,
  getNonDefaultOktaClientConfigurations,
  OktaClientConfiguration,
} from '@ssar-app/okta-authn-core';
import { useCallback, useEffect, useMemo } from 'react';
import { Route, Switch } from 'react-router';

const buildLoginRoute = (
  oktaClientConfiguration: OktaClientConfiguration,
  loginMountPath: string,
  renderLoginElement: Renderers['renderLoginElement'],
  onAuthenticated: OnAuthenticated,
) => {
  const key = `${oktaClientConfiguration.code}|${loginMountPath}`;
  return (
    <Route key={key} exact path={loginMountPath}>
      <>
        <OktaAuthnAppLogin
          renderLoginElement={renderLoginElement}
          onAuthenticated={onAuthenticated}
          loginMountPath={loginMountPath}
          oktaClientConfiguration={oktaClientConfiguration}
        ></OktaAuthnAppLogin>
      </>
    </Route>
  );
};
export type OktaAuthnAppLoginProps = LoginElementBuilderArgs & {
  loginMountPath: string;

  renderLoginElement: Renderers['renderLoginElement'];
};

// TODO: (CV) Check if the user already has a session, if so prompt the user.
export const OktaAuthnAppLogin = (props: OktaAuthnAppLoginProps) => {
  const { oktaClientConfiguration, renderLoginElement } = props;
  const { oktaClientCode: _currentOktaClientCode, setCurrentOktaClientCode } =
    useOktaAuthnAppContext();
  const oktaAuthManager = useOktaAuthManager();
  const oktaClientCode = oktaClientConfiguration.code;

  const oktaAuth = oktaAuthManager.getForOktaClientCode(oktaClientCode);
  useEffect(() => {
    if (oktaAuth) {
      setCurrentOktaClientCode(oktaClientCode);
    }
  }, [oktaAuth, oktaClientCode, setCurrentOktaClientCode]);

  if (!oktaAuth) {
    return <>Okta config/auth is missing.</>;
  } else {
    return renderLoginElement(props);
  }
};

export type OktaAuthnAppLoginListProps = Omit<
  LoginElementBuilderArgs,
  'oktaClientConfiguration'
> & {
  renderers: Renderers;
  oktaClientConfigurations: OktaClientConfiguration[];
};

export const OktaAuthnAppLoginList = (props: OktaAuthnAppLoginListProps) => {
  const { oktaClientConfigurations, renderers, onAuthenticated } = props;
  const { loginMountPath } = useOktaAuthnAppContext();
  const otherOktaClientConfigurations = getNonDefaultOktaClientConfigurations(
    oktaClientConfigurations,
  );
  const defaultOktaClientConfiguration =
    getDefaultOktaClientConfiguration(oktaClientConfigurations) ||
    otherOktaClientConfigurations[0];

  const buildLoginRouteFn = useCallback(
    (
      oktaClientConfiguration: OktaClientConfiguration,
      loginRoutePath: string,
    ) => {
      return buildLoginRoute(
        oktaClientConfiguration,
        loginRoutePath,
        renderers.renderLoginElement,
        onAuthenticated,
      );
    },
    [onAuthenticated, renderers.renderLoginElement],
  );

  const additionalRoutes = useMemo(() => {
    const toReturn = [];
    if (defaultOktaClientConfiguration) {
      toReturn.push(
        buildLoginRouteFn(
          defaultOktaClientConfiguration,
          buildSignInPath(loginMountPath),
        ),
        buildLoginRouteFn(
          defaultOktaClientConfiguration,
          buildSignInPath(loginMountPath, defaultOktaClientConfiguration.code),
        ),
      );
    }

    toReturn.push(
      buildFallbackRoute(
        `${loginMountPath || 'global'}-login-final-fallback-route`,
        renderers.renderNotFoundElement,
      ),
    );
    return toReturn;
  }, [
    loginMountPath,
    buildLoginRouteFn,
    defaultOktaClientConfiguration,
    renderers.renderNotFoundElement,
  ]);

  const loginRoutes = useMemo(
    () =>
      otherOktaClientConfigurations
        .filter((a) => a.code !== defaultOktaClientConfiguration?.code)
        .map((entry) => {
          return buildLoginRouteFn(
            entry,
            buildSignInPath(loginMountPath, entry.code),
          );
        })
        .concat(additionalRoutes),
    [
      additionalRoutes,
      buildLoginRouteFn,
      defaultOktaClientConfiguration?.code,
      loginMountPath,
      otherOktaClientConfigurations,
    ],
  );
  return (
    <>
      <Switch>{loginRoutes}</Switch>
    </>
  );
};
